/**
 * Converts a decimal number to a fraction.
 *
 * @param decimal - The decimal number to convert.
 * @returns An object containing the numerator and denominator of the fraction.
 */
export function decimalToFraction(decimal) {
    const whole = Math.floor(decimal);
    const fractionalPart = decimal - whole;
    if (fractionalPart === 0) {
        return { denominator: 1, numerator: 0, whole };
    }
    let bestNumerator = 0;
    let bestDenominator = 1;
    let minError = Math.abs(fractionalPart - bestNumerator / bestDenominator);
    for (let i = 1; i <= 1000; i++) {
        // Check denominators up to 1000
        for (let j = 0; j <= i; j++) {
            const currentFraction = j / i;
            const error = Math.abs(fractionalPart - currentFraction);
            if (error < minError) {
                minError = error;
                bestNumerator = j;
                bestDenominator = i;
                if (error < 0.000001) {
                    //Added a small error threshold
                    break; //Exit inner loop once a reasonable value is found
                }
            }
        }
        if (minError < 0.000001) {
            break; //Exit outer loop
        }
    }
    const divisor = gcd(bestNumerator, bestDenominator);
    return {
        denominator: bestDenominator / divisor,
        numerator: bestNumerator / divisor,
        whole,
    };
}
/**
 * Calculate the greatest common divisor of two numbers.
 *
 * @param a The first number.
 * @param b The second number.
 * @returns The greatest common divisor.
 */
export function gcd(a, b) {
    return b == 0 ? a : gcd(b, a % b);
}
/**
 * Get the aspect ratio of an image.
 *
 * @param width The width of the image.
 * @param height The height of the image.
 * @returns The aspect ratio.
 */
export function getAspectRatio(width, height) {
    const divisor = gcd(width, height);
    return `${width / divisor}:${height / divisor}`;
}
/**
 * Checks if the given value is numeric.
 *
 * @param value The value to check.
 * @returns A boolean value indicating whether the value is numeric.
 */
export function isNumeric(value) {
    if (typeof value === "number")
        return true;
    if (typeof value === "string")
        return /^-?\d+(?:\.\d+)?$/.test(value);
    return false;
}
/**
 * Round a number to a specific precision.
 *
 * @param value The value to round.
 * @param precision The number of decimal places to round to.
 * @returns The rounded value.
 */
export function toPrecision(value, precision) {
    const factor = Math.pow(10, precision);
    return Math.round(value * factor) / factor;
}
//# sourceMappingURL=numbers.js.map